import { useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FaArrowUp } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { uploadFileChunked } from "../services/uploadService";
import ProcessingStatus from "./ProcessingStatus";

const MAX_PDF_FILES = 500;

export default function FileUploader({ onClose, onUploadComplete }) {
  const [files, setFiles] = useState([]); // Array of files (ZIP or PDFs)
  const [fileType, setFileType] = useState(null); // 'zip' or 'pdf'
  const [folderName, setFolderName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [stage, setStage] = useState('idle'); // idle | bundling | uploading | processing | complete | error

  const fileInputRef = useRef(null);
  const location = useLocation();
  const locState = location.state || {};

  // Extract levels from location state
  let list = [];
  if (Array.isArray(locState.list)) {
    list = locState.list;
  } else if (locState && typeof locState === "object") {
    const src = locState.list && typeof locState.list === 'object' && locState.list.level1
      ? locState.list
      : locState;
    let data = src.state?.list;
    if (data) {
      list = data;
    }
  }

  const processSelectedFiles = useCallback((selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);

    // Check if it's a single ZIP file
    if (fileArray.length === 1 && fileArray[0].name.toLowerCase().endsWith(".zip")) {
      setFiles([fileArray[0]]);
      setFileType('zip');
      setError(null);
      if (!folderName) {
        setFolderName(fileArray[0].name.replace(/\.zip$/i, ""));
      }
      return;
    }

    // Check if all files are PDFs
    const pdfFiles = fileArray.filter(f => f.name.toLowerCase().endsWith(".pdf"));
    const nonPdfFiles = fileArray.filter(f => !f.name.toLowerCase().endsWith(".pdf"));

    if (nonPdfFiles.length > 0 && pdfFiles.length > 0) {
      setError("Please upload either a single ZIP file or multiple PDF files (not mixed)");
      return;
    }

    if (nonPdfFiles.length > 0) {
      setError("Please upload a ZIP file or PDF files only");
      return;
    }

    if (pdfFiles.length > MAX_PDF_FILES) {
      setError(`Maximum ${MAX_PDF_FILES} PDF files allowed. You selected ${pdfFiles.length}.`);
      return;
    }

    setFiles(pdfFiles);
    setFileType('pdf');
    setError(null);

    // Auto-generate folder name from first file if not set
    if (!folderName && pdfFiles.length > 0) {
      const firstName = pdfFiles[0].name.replace(/\.pdf$/i, "");
      setFolderName(pdfFiles.length > 1 ? `${firstName}_and_${pdfFiles.length - 1}_more` : firstName);
    }
  }, [folderName]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    processSelectedFiles(e.dataTransfer.files);
  }, [processSelectedFiles]);

  const handleFileSelect = (e) => {
    processSelectedFiles(e.target.files);
  };

  const bundlePdfsToZip = async (pdfFiles) => {
    const zip = new JSZip();

    for (const pdfFile of pdfFiles) {
      const arrayBuffer = await pdfFile.arrayBuffer();
      zip.file(pdfFile.name, arrayBuffer);
    }

    const zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 }
    }, (metadata) => {
      setUploadProgress({
        phase: 'bundling',
        percent: Math.round(metadata.percent),
        message: `Bundling PDFs... ${Math.round(metadata.percent)}%`,
        bytesUploaded: 0,
        totalBytes: 0
      });
    });

    // Create a File object from the Blob
    const zipFile = new File([zipBlob], `${folderName}.zip`, { type: "application/zip" });
    return zipFile;
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.warning("Please select files to upload");
      return;
    }

    if (!folderName.trim()) {
      toast.warning("Please enter project name");
      return;
    }

    const token = sessionStorage.getItem("userToken");
    if (!token) {
      toast.error("Authentication token missing");
      return;
    }

    // Build levels object
    const levels = {};
    for (let i = 0; i < 8; i++) {
      levels[`level${i + 1}`] = list[i] ?? "";
    }

    try {
      setUploading(true);
      setError(null);

      let fileToUpload;

      // If PDFs, bundle them into a ZIP first
      if (fileType === 'pdf') {
        setStage('bundling');
        setUploadProgress({
          phase: 'bundling',
          percent: 0,
          message: 'Preparing PDF files...',
          bytesUploaded: 0,
          totalBytes: 0
        });

        fileToUpload = await bundlePdfsToZip(files);
      } else {
        fileToUpload = files[0]; // Single ZIP file
      }

      setStage('uploading');

      // Chunked upload with progress
      const result = await uploadFileChunked({
        file: fileToUpload,
        folderName,
        levels,
        token,
        onProgress: (progress) => {
          setUploadProgress(progress);
        }
      });

      // Upload complete, switch to processing view
      setTaskId(result.task_id);
      setStage('processing');

    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.error || err.message || "Upload failed");
      setStage('error');
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleProcessingComplete = () => {
    setStage('complete');
    toast.success("Processing complete!");
    // Notify parent to refresh folder list immediately
    if (onUploadComplete) {
      onUploadComplete();
    }
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  const handleProcessingError = (errorMsg) => {
    setStage('error');
    setError(errorMsg);
    toast.error(errorMsg);
  };

  const clearSelection = () => {
    setFiles([]);
    setFileType(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Calculate total size
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  // Show processing view when we have a task ID
  if (stage === 'processing' && taskId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-xl font-semibold">Processing: {folderName}</h2>
          </div>
          <div className="p-6">
            <ProcessingStatus
              taskId={taskId}
              onComplete={handleProcessingComplete}
              onError={handleProcessingError}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Upload Files</h2>
          <button onClick={onClose} disabled={uploading}>
            <FaXmark />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">

          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              disabled={uploading}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Enter project name"
            />
          </div>

          {/* Dropzone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              files.length > 0 ? "border-green-600 bg-green-50" : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,.pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />

            <FaArrowUp className="w-10 h-10 mx-auto text-gray-400" />

            {files.length > 0 ? (
              <div className="mt-3">
                {fileType === 'zip' ? (
                  <>
                    <p className="font-medium">{files[0].name}</p>
                    <p className="text-sm text-gray-500">
                      {(totalSize / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">{files.length} PDF file{files.length > 1 ? 's' : ''} selected</p>
                    <p className="text-sm text-gray-500">
                      {(totalSize / (1024 * 1024)).toFixed(2)} MB total
                    </p>
                    {files.length <= 5 && (
                      <p className="text-xs text-gray-400 mt-1">
                        {files.map(f => f.name).join(', ')}
                      </p>
                    )}
                  </>
                )}
                {!uploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSelection();
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Clear selection
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="mt-3 font-medium">Drop files here</p>
                <p className="text-sm text-gray-500">ZIP archive or up to {MAX_PDF_FILES} PDF files</p>
              </>
            )}
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {uploadProgress.phase === 'bundling' && 'Bundling PDFs into archive...'}
                  {uploadProgress.phase === 'hashing' && 'Calculating hash...'}
                  {uploadProgress.phase === 'uploading' && (
                    uploadProgress.currentChunk
                      ? `Uploading chunk ${uploadProgress.currentChunk}/${uploadProgress.totalChunks}`
                      : 'Uploading...'
                  )}
                  {uploadProgress.phase === 'processing' && 'Finalizing...'}
                </span>
                <span className="font-medium">
                  {uploadProgress.percent}%
                  {uploadProgress.speed && ` (${uploadProgress.speed} MB/s)`}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.percent}%` }}
                />
              </div>
              {uploadProgress.bytesUploaded > 0 && (
                <p className="text-xs text-gray-500 text-right">
                  {(uploadProgress.bytesUploaded / (1024 * 1024)).toFixed(2)} MB /
                  {(uploadProgress.totalBytes / (1024 * 1024)).toFixed(2)} MB
                </p>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="border px-4 py-2 rounded-lg"
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || !folderName || uploading}
            className="bg-green-800 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {uploading ? (stage === 'bundling' ? "Bundling..." : "Uploading...") : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
